import type {
  AssetOutcomes,
  Band,
  CampaignCell,
  CampaignLogData,
  CampaignMetric,
  CampaignPart,
  CampaignTotals,
  EmailCampaign,
  EmailCampaignsData,
  EmailPatternGroup,
  EmailSend,
  EmailSendsData,
  ListHealthCounts,
  ListHealthRow,
  PatternBy,
  PerfData,
  PublishStatus,
  SplitArm,
  SplitComparison,
  SplitGroup,
  SplitMetric,
  SplitTrendRow,
  TagNamespace,
  TagTreeData,
  VoxShareRow,
} from "./api";

const COUNT = new Intl.NumberFormat("en-GB");

export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** One row on the Overview: a skeleton or a snippet, exactly as `/perf` scored it. */
export type AttentionRow = {
  kind: "skeleton" | "snippet";
  label: string;
  score: number;
  delta_pct: number | null;
  trend: string;
  draft_count: number;
  low_sample: boolean;
};

/**
 * Split `/perf` into the two Overview lists (UX S1). Declining records lead,
 * worst change first; improving records sit under Working well, best first.
 * Flat records are on neither list: Overview is "is anything wrong", not a
 * full table (that is Performance).
 */
export function attentionRows(perf: PerfData): { needsAttention: AttentionRow[]; workingWell: AttentionRow[] } {
  const rows: AttentionRow[] = [
    ...(perf.skeletons ?? []).map((s) => ({
      kind: "skeleton" as const,
      label: s.skeleton_slug,
      score: s.score,
      delta_pct: s.delta_pct,
      trend: s.trend,
      draft_count: s.draft_count,
      low_sample: s.low_sample,
    })),
    ...(perf.snippets ?? []).map((s) => ({
      kind: "snippet" as const,
      label: `Snippet #${s.snippet_id}`,
      score: s.score,
      delta_pct: s.delta_pct,
      trend: s.trend,
      draft_count: s.draft_count,
      low_sample: s.low_sample,
    })),
  ];
  const change = (r: AttentionRow) => r.delta_pct ?? 0;
  return {
    needsAttention: rows.filter((r) => r.trend === "down").sort((a, b) => change(a) - change(b)),
    workingWell: rows.filter((r) => r.trend === "up").sort((a, b) => change(b) - change(a)),
  };
}

function trendText(row: Pick<AttentionRow, "trend" | "delta_pct">): string {
  const pct = row.delta_pct === null ? "" : ` ${Math.abs(row.delta_pct)}%`;
  if (row.trend === "down") return `▼${pct}`;
  if (row.trend === "up") return `▲${pct}`;
  return "– flat";
}

/** A score never appears without its sample; under the threshold it is not shown at all. */
export function scoreLine(row: AttentionRow): string {
  const drafts = `${formatCount(row.draft_count)} draft${row.draft_count === 1 ? "" : "s"}`;
  if (row.low_sample) return `only ${drafts}, too few to read`;
  return `${Math.round(row.score)} · ${trendText(row)} · ${drafts}`;
}

/** The publish line never disappears: clean, pending, or never published (UX S11). */
export function publishLine(status: PublishStatus): string {
  if (status.has_unpublished && status.unpublished_count > 0) {
    const n = status.unpublished_count;
    return `${formatCount(n)} unpublished change${n === 1 ? "" : "s"}`;
  }
  if (status.live_version === null) return "Nothing has been published yet";
  return `Everything's published · v${status.live_version}`;
}

export function formatWhen(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

// ---- Performance ----

export const WINDOWS = ["7", "30", "90"] as const;
export type WindowDays = 7 | 30 | 90;
export const DEFAULT_DAYS: WindowDays = 30;

export function parseDays(value: unknown): WindowDays {
  return value === "7" || value === "90" ? (Number(value) as WindowDays) : DEFAULT_DAYS;
}

/** A `days`-long window ending today, inclusive, as the stats router's `YYYY-MM-DD` dates (UTC). */
export function dateRange(days: number, today: Date = new Date()): { date_from: string; date_to: string } {
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { date_from: start.toISOString().slice(0, 10), date_to: end.toISOString().slice(0, 10) };
}

export type StatsFilters = { days: WindowDays; run: string | null; medium: string | null };

/** The query string every `/stats/*` read shares, so one filter set drives the whole screen. */
export function statsQuery(filters: StatsFilters, today: Date = new Date()): string {
  const params = new URLSearchParams(dateRange(filters.days, today));
  if (filters.medium) params.set("format", filters.medium);
  if (filters.run) params.set("run_id", filters.run);
  return `?${params.toString()}`;
}

/** A fraction the server sent, as a percentage. `null` is a dash, never 0%. */
export function formatFraction(value: number | null | undefined): string {
  return value === null || value === undefined ? "–" : `${(value * 100).toFixed(1)}%`;
}

export function formatScore(value: number | null | undefined, digits = 1): string {
  return value === null || value === undefined ? "–" : value.toFixed(digits);
}

export type Tile = { key: string; label: string; value: string; detail: string; decider?: boolean };

const NO_STAMP = "No outcome data yet: no published asset has been stamped to a send.";
const NO_METRICS = "No outcome data yet: stamped sends have no email metrics yet.";

function outcomeTile(key: string, label: string, value: number | null | undefined, outcomes: AssetOutcomes): Tile {
  if (outcomes.outcome_status === "measured" && value !== null) {
    const n = outcomes.measured_broadcasts;
    return { key, label, value: formatFraction(value), detail: `${formatCount(n)} measured email broadcast${n === 1 ? "" : "s"}` };
  }
  const detail = outcomes.outcome_status === "awaiting_first_stamped_send" ? NO_STAMP : NO_METRICS;
  return { key, label, value: "–", detail };
}

function conversionTile(outcomes: AssetOutcomes): Tile {
  if (outcomes.outcome_status === "measured" && outcomes.conversion_rate !== null && outcomes.conversion_rate !== undefined) {
    const n = outcomes.orders_attributed;
    const orders =
      n === null || n === undefined
        ? "Attributed order count unavailable"
        : `${formatCount(n)} attributed order${n === 1 ? "" : "s"}`;
    const broadcasts = outcomes.measured_broadcasts;
    return {
      key: "cvr",
      label: "CVR",
      value: formatFraction(outcomes.conversion_rate),
      detail: `${orders} across ${formatCount(broadcasts)} measured email broadcast${broadcasts === 1 ? "" : "s"}`,
      decider: true,
    };
  }
  const detail = outcomes.outcome_status === "awaiting_first_stamped_send" ? NO_STAMP : NO_METRICS;
  return { key: "cvr", label: "CVR", value: "–", detail, decider: true };
}

/**
 * The six tiles (UX S2 section 3), in CX order with CVR last. Every figure is
 * the server's: the only arithmetic here is Rated %, the ratio of two counts
 * the tile shows beside it.
 */
export function performanceTiles(
  outcomes: AssetOutcomes,
  voxOverall: Pick<VoxShareRow, "sections" | "vox_sections" | "unedited_share"> | null,
): Tile[] {
  const voxShare: Tile =
    voxOverall && voxOverall.sections > 0
      ? {
          key: "vox_share",
          label: "VOX share",
          value: formatFraction(voxOverall.unedited_share),
          detail: `${formatCount(voxOverall.vox_sections)} of ${formatCount(voxOverall.sections)} rated sections sent as VOX drafted them`,
        }
      : { key: "vox_share", label: "VOX share", value: "–", detail: "No rated sections in this range" };
  const rated: Tile =
    outcomes.total_assets > 0
      ? {
          key: "rated",
          label: "Rated",
          value: formatFraction(outcomes.rated / outcomes.total_assets),
          detail: `${formatCount(outcomes.rated)} of ${formatCount(outcomes.total_assets)} assets`,
        }
      : { key: "rated", label: "Rated", value: "–", detail: "No assets in this range" };
  return [
    voxShare,
    rated,
    {
      key: "engine",
      label: "Avg Marketing Engine Score",
      value: formatScore(outcomes.avg_engine_score),
      detail: "1-10 AI rubric. No target band is set for this score yet.",
    },
    outcomeTile("open_rate", "Open Rate", outcomes.open_rate, outcomes),
    outcomeTile("ctr", "CTR", outcomes.click_through_rate, outcomes),
    conversionTile(outcomes),
  ];
}

// ---- Campaign Log ----

/** The verdicts the cvce campaign-log router accepts (`MARKETING_VERDICTS`). */
export const VERDICTS = ["PASS", "REVISE", "REJECT"] as const;

export type CampaignLogFilters = { days: WindowDays; verdict: string | null; format: string | null };

export function campaignLogParams(filters: CampaignLogFilters, today: Date = new Date()): string {
  const params = new URLSearchParams(dateRange(filters.days, today));
  if (filters.format) params.set("format", filters.format);
  if (filters.verdict && (VERDICTS as readonly string[]).includes(filters.verdict)) params.set("verdict", filters.verdict);
  return `?${params.toString()}`;
}

/** G-3: there is no campaign id yet, and an asset with no run says so. */
export function runLabel(runId: string | null): string {
  return runId ?? "Not linked to a run";
}

export function verdictLabel(verdict: string | null): string {
  if (!verdict) return "Not rated";
  return verdict.charAt(0) + verdict.slice(1).toLowerCase();
}

/**
 * The sentence shown instead of rows, or `null` when there are rows. A
 * telemetry failure is never presented as "no campaigns" (UX F2).
 */
export function campaignLogState(data: CampaignLogData, filtered: boolean): string | null {
  if (!data.available) return "We can't reach CVCE telemetry right now. Try again shortly.";
  if (data.campaign_log.length > 0) return null;
  return filtered
    ? "No assets match these filters. Try a wider window, another format, or another verdict."
    : "No campaigns logged yet.";
}

/** The band whose range holds the value, from the server's band set. No colour: the band is a word. */
export function bandFor(value: number | null | undefined, bands: Band[]): string | null {
  if (value === null || value === undefined) return null;
  return bands.find((b) => value >= b.low && value <= b.high)?.band ?? null;
}

// ---- Email (UX S12 / W8) ----
// Formatting only. Rates, bands, averages, totals, sample flags and the
// snapshot age all arrive from CVCE; nothing below divides, sums or averages.

export const EMAIL_DEFAULT_DAYS: WindowDays = 90;

export function parseEmailDays(value: unknown): WindowDays {
  return value === "7" || value === "30" || value === "90" ? (Number(value) as WindowDays) : EMAIL_DEFAULT_DAYS;
}

export type EmailFilters = {
  days: WindowDays;
  language: string | null;
  audience: string | null;
  campaign?: string | null;
  sendType?: EmailTypeFilter | null;
};

/** The Type filter: CVCE's send_type, newsletter or one-off broadcast. "All" is no value. */
export const EMAIL_TYPE_FILTERS = ["newsletter", "broadcast"] as const;
export type EmailTypeFilter = (typeof EMAIL_TYPE_FILTERS)[number];

export function parseEmailType(value: unknown): EmailTypeFilter | null {
  return (EMAIL_TYPE_FILTERS as readonly string[]).includes(value as string) ? (value as EmailTypeFilter) : null;
}

/** The query string the send and pattern reads share, so one filter set drives the tab. */
export function emailQuery(filters: EmailFilters, today: Date = new Date()): string {
  const params = new URLSearchParams(dateRange(filters.days, today));
  if (filters.language) params.set("language", filters.language);
  if (filters.audience) params.set("audience", filters.audience);
  if (filters.campaign) params.set("campaign", filters.campaign);
  if (filters.sendType) params.set("send_type", filters.sendType);
  return `?${params.toString()}`;
}

/** List health is the whole list: the window and language apply, audience cannot. */
export function listHealthQuery(days: WindowDays, language: string | null = null, today: Date = new Date()): string {
  const params = new URLSearchParams(dateRange(days, today));
  if (language) params.set("language", language);
  return `?${params.toString()}`;
}

export const EMAIL_VIEWS = ["overview", "campaigns", "languages", "sends", "split", "list", "patterns", "tags"] as const;
export type EmailView = (typeof EMAIL_VIEWS)[number];
export const EMAIL_VIEW_LABELS: Record<EmailView, string> = {
  overview: "Overview",
  campaigns: "Campaigns",
  languages: "Languages",
  sends: "Sends",
  split: "Split tests",
  list: "List health",
  patterns: "Patterns",
  tags: "Tags",
};

/** What the page filters reach on each view, stated on screen so nothing silently ignores a filter. */
export const EMAIL_FILTER_SCOPE: Record<EmailView, string> = {
  overview: "Window, campaign, language, audience and type all apply.",
  campaigns: "Window, campaign, language, audience and type all apply.",
  languages: "Window, audience and type apply. Campaign and language do not: this view is the whole grid.",
  sends: "Window, campaign, language, audience and type all apply.",
  split: "Window, campaign, language, audience and type all apply.",
  list: "Window and language apply. Campaign, audience and type do not: the subscriber list is not split by any of them.",
  patterns: "Window, campaign, language, audience and type all apply.",
  tags: "No filter applies: the tag tree is the whole list today and has no dates.",
};

export function parseEmailView(value: unknown): EmailView {
  return (EMAIL_VIEWS as readonly string[]).includes(value as string) ? (value as EmailView) : "overview";
}

export function channelHeadline(s: { sends: number; rated_sends: number; recipients: number | null }): string {
  const sends = `${formatCount(s.sends)} send${s.sends === 1 ? "" : "s"}`;
  const rated = `${formatCount(s.rated_sends)} with complete data and 50+ recipients`;
  return s.recipients === null ? `${sends} · ${rated}` : `${sends} · ${rated} · ${formatCount(s.recipients)} recipients`;
}

export type RateTile = { key: string; label: string; value: string; band: string | null; note?: string };

type RateKey =
  | "open_rate"
  | "click_rate"
  | "conversion_rate"
  | "click_to_open_rate"
  | "unsubscribe_rate"
  | "bounce_rate"
  | "complaint_rate"
  | "reply_rate";
type BandKey = `${RateKey}_band`;

type RateTileSpec =
  | { key: string; label: string; rate: RateKey; band: BandKey }
  | { key: string; label: string; rate: "revenue_per_recipient" };

const RATE_TILES: RateTileSpec[] = [
  { key: "open", label: "Open Rate", rate: "open_rate", band: "open_rate_band" },
  { key: "ctr", label: "CTR", rate: "click_rate", band: "click_rate_band" },
  { key: "cvr", label: "CVR", rate: "conversion_rate", band: "conversion_rate_band" },
  { key: "revenue", label: "Revenue / recipient", rate: "revenue_per_recipient" },
  { key: "cto", label: "Click-to-open", rate: "click_to_open_rate", band: "click_to_open_rate_band" },
  { key: "unsub", label: "Unsubscribe", rate: "unsubscribe_rate", band: "unsubscribe_rate_band" },
  { key: "bounce", label: "Bounce", rate: "bounce_rate", band: "bounce_rate_band" },
  { key: "complaint", label: "Complaint", rate: "complaint_rate", band: "complaint_rate_band" },
  { key: "reply", label: "Reply rate", rate: "reply_rate", band: "reply_rate_band" },
];

type RateSource = Pick<
  EmailSend,
  RateKey | BandKey | "orders_attributed" | "revenue_per_recipient" | "revenue_per_recipient_band"
>;

function formatRevenuePerRecipient(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `£${value.toFixed(3)}`;
}

/** Rate tiles for a send or the channel summary, each with the band CVCE assigned (null when no target is set). */
export function emailRateTiles(send: RateSource): RateTile[] {
  return RATE_TILES.map((t) => {
    if (t.rate === "revenue_per_recipient") {
      return {
        key: t.key,
        label: t.label,
        value: formatRevenuePerRecipient(send[t.rate]),
        band: send.revenue_per_recipient_band ?? null,
      };
    }
    const orders = send.orders_attributed;
    return {
      key: t.key,
      label: t.label,
      value: formatFraction(send[t.rate]),
      band: send[t.band] ?? null,
      note:
        t.rate === "conversion_rate" && orders !== null && orders !== undefined
          ? `${formatCount(orders)} attributed order${orders === 1 ? "" : "s"}`
          : undefined,
    };
  });
}

/** Webhook rows are event-level; backfilled rows were copied from Drip's report. */
export function sendSourceLabel(source: string | null): string {
  if (source === "webhook") return "Streamed";
  if (source === "backfill_ui") return "Backfilled";
  return source ?? "Source not recorded";
}

export function backfillNote(send: EmailSend): string | null {
  if (send.source !== "backfill_ui") return null;
  const when = send.computed_at ? send.computed_at.slice(0, 10) : "an unknown date";
  return `Numbers copied from Drip's report on ${when}; not event-level.`;
}

/** `completeness` is CVCE's verdict; this only words it. */
export function completenessNote(send: Pick<EmailSend, "completeness">): string | null {
  if (send.completeness === "counting") return "Still counting";
  if (send.completeness === "incomplete") return "Incomplete: events missing";
  return null;
}

/** The send's funnel (Precision Funnel C10b): display only, the per-funnel cut lives in the Funnels module. */
export function funnelText(send: Pick<EmailSend, "funnel_slug">): string {
  if (send.funnel_slug === "none") return "No funnel";
  return send.funnel_slug ?? "—";
}

/** The automated content eval of the asset behind a send, as CVCE scored it. */
export function evalText(send: Pick<EmailSend, "asset_id" | "eval_score" | "eval_verdict" | "eval_passes">): string {
  if (!send.asset_id || send.eval_score === null || send.eval_score === undefined) return "–";
  const passes = send.eval_passes ? ` · ${formatCount(send.eval_passes)} pass${send.eval_passes === 1 ? "" : "es"}` : "";
  return `${formatScore(send.eval_score)}${send.eval_verdict ? ` ${send.eval_verdict}` : ""}${passes}`;
}

/** The human review of the asset behind a send: findings count and who reviewed it. */
export function reviewText(send: Pick<EmailSend, "asset_id" | "review_findings" | "reviewer">): string {
  if (!send.asset_id) return "–";
  if (send.review_findings === null || send.review_findings === undefined) return "Not reviewed";
  const n = send.review_findings;
  const found = n === 0 ? "No findings" : `${formatCount(n)} finding${n === 1 ? "" : "s"}`;
  return send.reviewer ? `${found} · ${send.reviewer}` : found;
}

export function languageLabel(language: string | null): string {
  return language ? language.toUpperCase() : "No language";
}

const ESP_LABELS: Record<string, string> = { drip: "Drip", ses: "Amazon SES", mixed: "Mixed" };
const SEND_TYPE_LABELS: Record<string, string> = { newsletter: "Newsletter", broadcast: "Broadcast", mixed: "Mixed" };

/** CVCE's ESP code as a column label; a dash when CVCE did not send one. */
export function espLabel(esp: string | null | undefined): string {
  return esp ? (ESP_LABELS[esp] ?? esp) : "–";
}

/** When a campaign went out: one send's time, or the first-to-last range and the send count.
 * Several sends always show their count, even when they share one start time. */
export function campaignWhen(c: { first_send_at: string | null; last_send_at?: string | null; send_count?: number }): string {
  const first = formatWhen(c.first_send_at) ?? "–";
  const count = c.send_count ?? 1;
  if (count <= 1) return first;
  const range = c.last_send_at && c.last_send_at !== c.first_send_at ? `${first} to ${formatWhen(c.last_send_at) ?? "–"}` : first;
  return `${range} · ${formatCount(count)} sends`;
}

/** The send job's own Sent count: a dash for Drip (no sent events), "so far" while an SES send is still going. */
export function sentText(x: { sent?: number | null; sending?: boolean | null }): string {
  if (x.sent === null || x.sent === undefined) return "–";
  return x.sending ? `${formatCount(x.sent)} so far` : formatCount(x.sent);
}

/** CVCE's send type as a column label; a dash when CVCE did not send one. */
export function sendTypeLabel(sendType: string | null | undefined): string {
  return sendType ? (SEND_TYPE_LABELS[sendType] ?? sendType) : "–";
}

/** "Why your ketones drop at 3pm · 24 Sep 2026, 07:00 · EN · 12,428 recipients". */
export function sendHeadline(send: EmailSend): string {
  const when = formatWhen(send.send_at) ?? send.send_date;
  return [send.broadcast_name ?? send.broadcast_id, when, languageLabel(send.language), `${formatCount(send.recipients)} recipients`].join(" · ");
}

export function latencyText(hours: number | null): string {
  if (hours === null || hours === undefined) return "–";
  return hours < 1 ? `${Math.round(hours * 60)} min` : `${hours.toFixed(1)} h`;
}

/**
 * The sentence shown instead of the send list, or `null` when there are sends.
 * A read failure is never presented as "no sends" (UX S12 states).
 */
export function emailSendsState(data: EmailSendsData, filters: EmailFilters): string | null {
  if (!data.available) return "Send scorecard unavailable: CVCE could not read BigQuery. Try again shortly.";
  if (data.sends.length > 0) return null;
  if (filters.campaign) return "No sends in this campaign for this range. Try a wider date range or clear the campaign filter.";
  if (filters.sendType) return `No ${filters.sendType} sends for this range. Try a wider date range or set Type to All.`;
  if (filters.language || filters.audience) {
    const what = filters.language ? languageLabel(filters.language) : "this audience";
    return `No sends in ${what} for this range. Try a wider date range or another language.`;
  }
  return "No email sends recorded yet. Sends appear here the hour after they go out.";
}

export const PATTERN_LABELS: Record<PatternBy, string> = {
  language: "By Language",
  audience: "By Audience",
  resend: "Original vs Resend",
};

export function patternKeyLabel(by: PatternBy, key: string | null): string {
  if (key === null || key === "") return "Not recorded";
  return by === "language" ? languageLabel(key) : key;
}

/** CVCE flags a group with fewer than two rated sends; its averages are not a comparison. */
export function patternNote(group: EmailPatternGroup): string | null {
  if (!group.too_few_to_compare) return null;
  if (group.sends === 1) return "1 send, too few to compare";
  const n = group.rated_sends;
  return `${n} send${n === 1 ? "" : "s"} with 50+ recipients, too few to compare`;
}

/** "42,160 subscribers · +48 in 7 days", from CVCE's per-day totals. */
export function listHealthHeadline(days: ListHealthCounts[]): string | null {
  const latest = days.at(-1);
  if (!latest) return null;
  return `${formatCount(latest.subscribers)} subscribers · +${formatCount(latest.new_7d)} in 7 days`;
}

export function listStaleLine(ageDays: number | null | undefined): string | null {
  if (ageDays === null || ageDays === undefined || ageDays <= 2) return null;
  return `List snapshot is ${formatCount(ageDays)} days old`;
}

export function tagTreeSummary(data: TagTreeData): string {
  const parts = [
    `${formatCount(data.total_tags ?? 0)} tags`,
    `${formatCount(data.namespace_count ?? 0)} groups`,
    `${formatCount(data.unclassified_count ?? 0)} not yet grouped`,
  ];
  return parts.join(" · ");
}

/** A private namespace is only ever a count: its tag text never reaches the browser. */
export function namespaceLine(ns: TagNamespace): string {
  if (ns.private) return `private survey answers: ${formatCount(ns.tag_count)} (hidden)`;
  return `${ns.namespace} ${formatCount(ns.tag_count)}`;
}

export type LanguageSeries = { language: string | null; points: { date: string; subscribers: number | null }[] };

/**
 * One series per language over every snapshot date CVCE returned. A day a
 * language has no row is `null` (a gap in the line), never 0: a missing
 * snapshot is not an empty list. Reshaping only; no value is derived.
 */
export function languageSeries(rows: ListHealthRow[]): LanguageSeries[] {
  const dates = [...new Set(rows.map((r) => r.snapshot_date))].sort();
  const byLanguage = new Map<string | null, Map<string, number>>();
  for (const r of rows) {
    const series = byLanguage.get(r.language) ?? new Map<string, number>();
    series.set(r.snapshot_date, r.subscribers);
    byLanguage.set(r.language, series);
  }
  return [...byLanguage.entries()].map(([language, series]) => ({
    language,
    points: dates.map((date) => ({ date, subscribers: series.get(date) ?? null })),
  }));
}

// ---- Split tests (KET-6747): one send, two cohorts, two versions ----

export const SPLIT_METRIC_LABELS: Record<SplitMetric, string> = {
  click_rate: "Click rate",
  click_to_open_rate: "Click-to-open",
  open_rate: "Open rate",
  unsubscribe_rate: "Unsubscribe rate",
};

/** Under this many recipients a rate still shows but gets no band colour (one unsubscribe is not Critical). */
export const BAND_MIN_RECIPIENTS = 100;

/** "Cohort A · marketing__email_one_action_b · mechanism · image: reason". */
export function armLabel(arm: Pick<SplitArm, "cohort" | "skeleton_slug" | "variant" | "image_slot">): string {
  const parts = [arm.cohort ? `Cohort ${arm.cohort}` : "Whole list", arm.skeleton_slug ?? "version not stamped"];
  if (arm.variant) parts.push(arm.variant);
  if (arm.image_slot) parts.push(`image: ${arm.image_slot}`);
  return parts.join(" · ");
}

/** A rate difference in percentage points, signed: "+4.0 pts". */
export function formatPoints(value: number | null): string {
  if (value === null) return "–";
  const pts = value * 100;
  return `${pts > 0 ? "+" : pts < 0 ? "−" : ""}${Math.abs(pts).toFixed(1)} pts`;
}

/** The verdict for one metric of an A-vs-B pair, in words. */
export function comparisonVerdict(c: SplitComparison): string {
  if (c.a_rate === null || c.b_rate === null || c.ci_low === null || c.ci_high === null) return "not enough data to compare";
  const range = `95% range ${formatPoints(c.ci_low)} to ${formatPoints(c.ci_high)}`;
  if (c.leader === null) return `no winner yet (${range})`;
  return `${c.leader} wins by ${formatPoints(Math.abs(c.diff ?? 0)).replace(/^[+−]/, "")} (${range})`;
}

/** Why a send has no comparison, when it has none. */
export function splitGroupNote(group: SplitGroup): string | null {
  if (group.comparison) return null;
  if (group.arms.length === 1 && !group.arms[0].cohort) return "One version went to the whole audience, so there is nothing to compare.";
  if (group.arms.length === 1) return `Only cohort ${group.arms[0].cohort}'s half of this send is here, so there is nothing to compare yet.`;
  return "This send has no single cohort A and cohort B pair, so no comparison is made.";
}

export function splitGroupHeadline(group: SplitGroup): string {
  const when = formatWhen(group.send_at) ?? group.week ?? "date unknown";
  const arms = `${group.arms.length} version${group.arms.length === 1 ? "" : "s"}`;
  return [group.campaign_key, languageLabel(group.language), when, arms].join(" · ");
}

/** Band for display: none when the arm is under the recipient floor. */
export function armBand(arm: SplitArm, metric: SplitMetric): string | null {
  return arm.band_masked || arm.recipients < BAND_MIN_RECIPIENTS ? null : arm[`${metric}_band`];
}

export type SplitTrendSeries = { key: string; label: string };

/** Pivot the weekly trend into one chart row per week, one series per version x cohort. */
export function splitTrendChart(rows: SplitTrendRow[], metric: SplitMetric = "click_rate"): {
  points: Array<Record<string, string | number | null>>;
  series: SplitTrendSeries[];
} {
  const labels = new Map<string, string>();
  // Chart keys are positional (s0, s1, ...): Recharts reads "." or "[" in a dataKey as a path.
  const series = new Map<string, string>();
  const byWeek = new Map<string, Record<string, string | number | null>>();
  for (const row of rows) {
    const id = [row.skeleton_slug ?? "-", row.variant ?? "-", row.cohort ?? "-"].join("|");
    if (!series.has(id)) series.set(id, `s${series.size}`);
    const key = series.get(id)!;
    labels.set(key, armLabel({ ...row, image_slot: null }));
    const point = byWeek.get(row.week) ?? { week: row.week };
    point[key] = row[metric];
    byWeek.set(row.week, point);
  }
  const points = [...byWeek.values()].sort((a, b) => String(a.week).localeCompare(String(b.week)));
  return { points, series: [...labels].map(([key, label]) => ({ key, label })) };
}

// ---- Email by campaign (2026-10-01) ----
// Every total, rate and band below is CVCE's (/email/campaigns). This file only
// orders, labels and picks; it never sums or divides.

/** The bucket for sends with no stamp whose name breaks the naming rule. */
export const CAMPAIGN_NONE = "none";

/** English first, then alphabetical: English is the big list every other language is read against. */
export function sortLanguages(languages: (string | null)[]): (string | null)[] {
  return [...languages].sort((a, b) => (a === "en" ? -1 : b === "en" ? 1 : a === null ? 1 : b === null ? -1 : a.localeCompare(b)));
}

export function campaignOptionLabel(c: EmailCampaign): string {
  if (c.campaign_key === CAMPAIGN_NONE) return `Not grouped (${formatCount(c.sends)} ${c.sends === 1 ? "send" : "sends"})`;
  return `${c.label} (${c.languages.length} ${c.languages.length === 1 ? "language" : "languages"})`;
}

/**
 * The Campaign filter's options. A selected campaign with no sends in the current
 * window stays listed, so the filter never reads "All" while it is still applied.
 */
export function campaignFilterOptions(campaigns: EmailCampaign[], selected: string | null, loaded = true): { value: string; label: string }[] {
  const options = campaigns.map((c) => ({ value: c.campaign_key, label: campaignOptionLabel(c) }));
  if (selected && !campaigns.some((c) => c.campaign_key === selected)) {
    const name = selected === CAMPAIGN_NONE ? "Not grouped" : selected;
    // Only a list that loaded can say there are no sends; a loading or failed read says nothing.
    options.push({ value: selected, label: loaded ? `${name} (no sends in this window)` : name });
  }
  return options;
}

/** "Sent 30 Sep 2026, 07:00 in 7 languages · 34,864 recipients · resent to unopened 26 Sep 2026, 06:24". */
export function campaignHeadline(c: EmailCampaign): string {
  const parts = [
    `Sent ${formatWhen(c.first_send_at) ?? "–"} in ${c.languages.length} ${c.languages.length === 1 ? "language" : "languages"}`,
    `${formatCount(c.first.recipients)} recipients`,
  ];
  if (c.resend) parts.push(`resent to unopened ${formatWhen(c.resend_at) ?? ""} (${formatCount(c.resend.recipients)} recipients)`.replace("  ", " "));
  return parts.join(" · ");
}

/** Shown while any send in the campaign is still collecting events: early rates read low. */
export function campaignCompletenessNote(c: EmailCampaign): string | null {
  return c.data_complete ? null : "Still counting: some sends in this campaign are still collecting opens and clicks, so these numbers can move.";
}

const CAMPAIGN_TILES: { key: string; label: string; rate: CampaignMetric }[] = [
  { key: "open", label: "Open Rate", rate: "open_rate" },
  { key: "ctr", label: "CTR", rate: "click_rate" },
  { key: "unsub", label: "Unsubscribe", rate: "unsubscribe_rate" },
  { key: "reply", label: "Reply rate", rate: "reply_rate" },
];

/** Rate tiles for a campaign total. A band only shows at 100+ recipients (CVCE masks it below). */
export function campaignRateTiles(t: CampaignTotals): RateTile[] {
  return CAMPAIGN_TILES.map((x) => ({
    key: x.key,
    label: x.label,
    value: formatFraction(t[x.rate]),
    band: t.band_masked ? null : t[`${x.rate}_band`],
    ...(t.band_masked ? { note: "Under 100 recipients: no band" } : {}),
  }));
}

export function cellBand(t: CampaignTotals | null, metric: CampaignMetric): string | null {
  return t && !t.band_masked ? t[`${metric}_band`] : null;
}

export function campaignCell(c: EmailCampaign, language: string | null, part: CampaignPart): CampaignCell | null {
  return c.cells.find((cell) => cell.language === language && cell.part === part) ?? null;
}

/**
 * The two sentences that stop a campaign total being misread: how much of it is
 * English, and how the other languages did on their own. Values are CVCE's.
 */
export function campaignInsights(c: EmailCampaign): string[] {
  const out: string[] = [];
  const en = campaignCell(c, "en", "first");
  if (c.english_share !== null && en) {
    out.push(
      c.english_share > 0.5
        ? `English is ${formatFraction(c.english_share)} of recipients, so the campaign total (${formatFraction(c.first.open_rate)} open) is mostly the English result.`
        : `English is ${formatFraction(c.english_share)} of recipients, so the campaign total (${formatFraction(c.first.open_rate)} open) is mostly the other languages.`,
    );
  }
  if (c.non_english_first && en) {
    out.push(
      `The other languages together opened at ${formatFraction(c.non_english_first.open_rate)} on ${formatCount(c.non_english_first.recipients)} recipients, against ${formatFraction(en.open_rate)} for English.`,
    );
  }
  if (!en) out.push("No English send in this selection. Read the language rows, not only the total.");
  return out;
}

export const CAMPAIGN_METRIC_LABELS: Record<CampaignMetric, string> = {
  open_rate: "Open",
  click_rate: "Click (CTR)",
  unsubscribe_rate: "Unsubscribe",
  reply_rate: "Reply",
};

export type LanguageGridRow = {
  key: string;
  label: string;
  cells: (CampaignCell | null)[];
  total: CampaignTotals | null;
  nonEnglish: CampaignTotals | null;
};

/**
 * Campaign x language: one row per grouped campaign, one column per language,
 * plus the campaign total and (first sends only) the total without English.
 * The "not grouped" bucket is left out: it has no single campaign to read.
 */
export function languageGrid(data: EmailCampaignsData, part: Exclude<CampaignPart, "resend">) {
  const grouped = data.campaigns.filter((c) => c.campaign_key !== CAMPAIGN_NONE);
  const seen = new Set<string | null>();
  grouped.forEach((c) => c.cells.forEach((cell) => seen.add(cell.language)));
  const languages = sortLanguages([...seen]);
  const rows: LanguageGridRow[] = grouped.map((c) => ({
    key: c.campaign_key,
    label: c.label,
    cells: languages.map((l) => campaignCell(c, l, part)),
    total: part === "first" ? c.first : c.all,
    nonEnglish: part === "first" ? c.non_english_first : null,
  }));
  const footer = languages.map((l) => data.by_language.find((cell) => cell.language === l && cell.part === part) ?? null);
  return { languages, rows, footer };
}

export type SendGroup = { key: string; label: string; sends: EmailSend[]; campaign: EmailCampaign | null };

/** The send list folded under its campaign, in the campaign order CVCE returned; ungrouped sends last. */
export function groupSendsByCampaign(sends: EmailSend[], campaigns: EmailCampaign[]): SendGroup[] {
  const keyOf = (s: EmailSend) =>
    !s.campaign_key || s.campaign_source === "none" || s.campaign_key.startsWith("unstamped:") ? CAMPAIGN_NONE : s.campaign_key;
  const order = campaigns.map((c) => c.campaign_key);
  const groups = new Map<string, EmailSend[]>();
  sends.forEach((s) => {
    const k = keyOf(s);
    groups.set(k, [...(groups.get(k) ?? []), s]);
  });
  const keys = [...groups.keys()].sort((a, b) => {
    const ia = a === CAMPAIGN_NONE ? Infinity : order.indexOf(a) === -1 ? order.length : order.indexOf(a);
    const ib = b === CAMPAIGN_NONE ? Infinity : order.indexOf(b) === -1 ? order.length : order.indexOf(b);
    return ia - ib;
  });
  return keys.map((k) => {
    // The "none" bucket is unrelated sends: it gets no campaign totals in its header.
    const campaign = k === CAMPAIGN_NONE ? null : (campaigns.find((c) => c.campaign_key === k) ?? null);
    return { key: k, label: k === CAMPAIGN_NONE ? "Not grouped" : (campaign?.label ?? k), sends: groups.get(k)!, campaign };
  });
}

/** The warning under the campaign list when some sends could not be grouped. */
export function ungroupedNote(c: EmailCampaign | undefined): string | null {
  if (!c) return null;
  return `${formatCount(c.sends)} ${c.sends === 1 ? "send is" : "sends are"} not grouped: no campaign stamp and a name that breaks the rule "<YYMM> <Topic> <LANG>". Rename in Drip or stamp with drip.py to group ${c.sends === 1 ? "it" : "them"}.`;
}
